import React, { useCallback, useMemo, useState } from 'react';
import { MdArrowDropDown, MdArrowDropUp } from 'react-icons/md';

import moment from 'moment';

import Tooltip from 'components/Tooltips/Tooltip';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { ResultPrice } from 'models/auto/price';
import { RatesSignalTypes, RateUI } from 'models/auto/vehicleRates';
import TagPricesDetail from 'screens/components/TravelDetails/components/TagPricesDetail';
import { getFormattedPrice } from 'utils/currency';
import { isNullOrUndefined } from 'utils/util';

import { Rates, Card, Rate, InfoCircleIcon } from './styles';
import { mapPriceToRates } from './utils/mapper';

export interface Props {
  rates: ResultPrice;
  pickUp?: Date;
  dropOff?: Date;
  daysQty?: number;
  priceAtDestination?: number;
  total?: number;
  isHistory?: boolean;
  status?: string;
  time?: { hours: number; minutes: number };
  hideTooltip?: boolean;
}
const AutoDetailRates = ({
  rates,
  pickUp,
  dropOff,
  priceAtDestination = 0,
  total = 0,
  daysQty = 0,
  isHistory = false,
  status,
  time,
  hideTooltip = false,
}: Props) => {
  const { t } = useStoreTranslation();
  const [active, setActive] = useState(true);
  const currency = rates.currencyCode || '';
  const formattedPrice = (value: number | undefined) => {
    const { formatted } = getFormattedPrice({ value, currency, t });
    return formatted;
  };
  const toggle = useCallback(() => setActive(!active), [active, setActive]);
  const signal = (s) => (s === RatesSignalTypes.SUB ? '-' : s === RatesSignalTypes.MULTIPLY ? '*' : '');
  const ratesUI: RateUI[] = mapPriceToRates(rates, priceAtDestination, total);
  const period = useMemo(() => {
    const days = dropOff && pickUp ? moment(dropOff).diff(moment(pickUp), 'days') : daysQty;
    return days > 1 ? `${t('autosDetails.rates.daysRent', { period: days })}` : `${t('autosDetails.rates.dayRent')}`;
  }, [t, dropOff, pickUp, daysQty]);

  return (
    <Rates>
      <Rates.Header>
        <h3>{t('autosDetails.rates.title')}</h3>
        <p>{t('autosDetails.rates.text')}</p>
      </Rates.Header>

      <Card>
        {isHistory ? (
          <Card.Header>
            <Card.Div>
              <Card.Title>{t('travelDetails.prices.title')}</Card.Title>
              {status && time && <TagPricesDetail status={status} time={time} />}
            </Card.Div>
            <Card.Div>
              <Card.Subtitle>{period}</Card.Subtitle>
            </Card.Div>
          </Card.Header>
        ) : (
          <Card.Header onClick={toggle}>
            <Card.Title>{period}</Card.Title>
            <Card.Icon>{!active ? <MdArrowDropDown /> : <MdArrowDropUp />}</Card.Icon>
          </Card.Header>
        )}
        <Card.Body $active={active}>
          {active ? (
            <Card.Rates>
              {ratesUI.map((rate, idx) => (
                <Rate key={idx} className={rate.className}>
                  <Rate.Label className={rate.className} id={rate.className}>
                    {t(rate.label)}
                    {rate.className === 'agency' && !hideTooltip ? (
                      <Tooltip event="click" content={t('autosDetails.rates.tooltip')}>
                        <InfoCircleIcon />
                      </Tooltip>
                    ) : null}
                  </Rate.Label>
                  <Rate.Value className={rate.className}>
                    {signal(rate.signal)}
                    {!isNullOrUndefined(rate.value) ? formattedPrice(rate.value) : null}
                  </Rate.Value>
                </Rate>
              ))}
            </Card.Rates>
          ) : null}
        </Card.Body>
      </Card>
    </Rates>
  );
};

export default AutoDetailRates;
